import { memo, ReactElement, useCallback, useMemo } from 'react';
import Image from 'next/image';
import clsx from 'clsx';

import { useToggleCoupon } from '~/hooks/account';
import { useCart } from '~/lib/cart';
import { formatStandardCalendarDate } from '~/lib/util/helpers';
import { CouponType } from '~/types/account';

import {
  ApplyCartButton,
  RemoveCartButton,
} from '~/components/account/Discounts';
import ErrorCircle from '~/public/font-awesome/exclamation-circle-red.png';

const CouponDisplay = memo(
  ({
    coupon,
    isCurrentCouponApplied,
    isAnyCouponApplied,
    className,
  }: {
    coupon: CouponType;
    isCurrentCouponApplied: boolean;
    isAnyCouponApplied?: boolean;
    className?: string;
  }): ReactElement => {
    const { applyCoupon, unapplyCoupon } = useCart();
    const { isApplyingCoupon, isUnapplyingCoupon, errorMessage, toggleCoupon } =
      useToggleCoupon(applyCoupon, unapplyCoupon);

    const expirationDate =
      formatStandardCalendarDate(coupon.end_date || coupon.campaign_end_date) ||
      'Never';

    const startDate = useMemo(
      () => formatStandardCalendarDate(coupon.campaign_start_date) || 'TBD',
      [coupon.campaign_start_date],
    );

    const onClickToggle = useCallback(
      (applyState: boolean) => toggleCoupon(applyState, coupon),
      [coupon, toggleCoupon],
    );

    let couponUsage = 'Single-use coupon'; // default
    if (coupon?.user_limit === 0) {
      couponUsage = `${coupon.status} (${coupon.usage_display})`;
    } else if (coupon?.user_limit > 1) {
      couponUsage = `${coupon.status} for ${coupon.user_limit} purchases (used ${coupon.usage_display})`;
    }

    return (
      // wrapper div is necessary here to allow proper gap setup from parent
      <div className={className} data-testid="saved-coupon">
        <div
          className={clsx(
            'flex flex-col justify-between rounded border p-4 md:flex-row',
            errorMessage ? 'border-2 border-brick' : 'border-sundog',
          )}
        >
          <div className="text-center md:text-left">
            <p className="font-bold">{coupon.description}</p>
            <p className="text-grace">Expires:&nbsp;{expirationDate}</p>
            <p className="text-grace">{couponUsage}</p>
          </div>

          {!coupon.isComingSoon ? (
            <div className="flex flex-col justify-center md:gap-2">
              <ApplyCartButton
                isApplied={isCurrentCouponApplied}
                isLoading={isApplyingCoupon}
                onClick={onClickToggle}
                isDisabled={isAnyCouponApplied}
              />
              <RemoveCartButton
                isApplied={isCurrentCouponApplied}
                isLoading={isUnapplyingCoupon}
                onClick={onClickToggle}
              />
            </div>
          ) : (
            <div className="mt-2 text-center text-sm md:mt-0 md:text-right">
              <span className="inline-block rounded bg-iceberg px-2 py-1 font-medium text-midnight md:mb-2">
                Coming soon!
              </span>
              <span className="block text-grace">Available: {startDate}</span>
            </div>
          )}
        </div>
        {!!errorMessage && (
          <p className="mt-2 flex items-center text-sm text-brick">
            <Image
              height={16}
              width={16}
              src={ErrorCircle}
              alt="error-circle"
              className="mr-2 inline-block"
            />
            <span>{errorMessage}</span>
          </p>
        )}
      </div>
    );
  },
);

CouponDisplay.displayName = 'CouponDisplay';
export default CouponDisplay;
